/**
 * Web continuation of the Android app's splash screen.
 *
 * Chrome drops the native splash at the page's first paint, which used to be an empty white page
 * for ~1s. On the app's launch URL (`/apphome?application=true`) `_document` server-renders the
 * native splash's hexagon — inlined, same size and place — so the hand-off is invisible. Then
 * (owner, 2026-10-12) the hexagon shrinks, moves right and «پذیرش۲۴» in IRANSans comes in beside
 * it, like the site header; the site's gray three-dot loader waits at the bottom, and once home is
 * ready the splash fades into it. No other request renders it.
 */

export const APP_SPLASH_ID = 'app-splash';
/** Last-resort cap so a failed chunk can never leave the app stuck behind the splash. */
const MAX_VISIBLE_MS = 6000;

/**
 * First app build whose native splash matches this one (logo hexagon only). Older builds show
 * their own, different splash, so they must not get the web continuation (owner, 2026-10-13).
 */
export const APP_SPLASH_MIN_VERSION_CODE = 9100;

/**
 * Doctor devices go straight to doctor home (/_/) from the launch page. On a splash launch the
 * redirect script (doctorDeviceCache.ts) only stores the target here, and the splash navigates
 * there once its animation is done, so doctors see the same splash (owner, 2026-10-13).
 */
export const APP_SPLASH_DOCTOR_HOME_KEY = '__p24SplashDoctorHome';

export const shouldRenderAppSplash = (page: string, query: Record<string, unknown> | undefined) =>
  page === '/apphome' &&
  query?.application === 'true' &&
  Number(query?.version_code) >= APP_SPLASH_MIN_VERSION_CODE;

export const APP_SPLASH_STYLE = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483647,
  background: '#fff',
} as const;

/**
 * Where the native splash drew the logo: the centre of the whole screen. The page viewport starts
 * below the top system bar and its height is still settling during launch, so "50% of the
 * viewport" drifted by up to ~30px. The app passes the top bar height as `stop` (dp == CSS px;
 * LauncherActivity.getLaunchingUrl), so the logo goes at screen.height/2 − stop from the
 * viewport top, which doesn't move. Without it (old app builds) the viewport centre is used.
 * Also used verbatim by scripts/gen-app-launch-shell.js.
 */
export const APP_SPLASH_ALIGN_JS =
  "try{var m=/[?&]stop=([0-9.]+)/.exec(location.search);if(m)document.documentElement.style.setProperty('--app-splash-cy',(screen.height/2-m[1])+'px')}catch(e){}";

/** Hexagon on the native splash (labs/paziresh24-twa splash/splash_system_icon), in dp == CSS px. */
export const APP_SPLASH_HEX = { width: 90.375, height: 96 } as const;

export const APP_SPLASH_FONT_URL = '/fonts/IRANSansXFaNum-Black.woff2';

/**
 * Splash styles, shared verbatim with scripts/gen-app-launch-shell.js. The hexagon starts exactly
 * where the native splash drew it; .app-splash-on runs the move. Loader = atom/loading in gray.
 */
export const APP_SPLASH_CSS = `@font-face{font-family:P24Splash;src:url(${APP_SPLASH_FONT_URL}) format('woff2');font-weight:900;font-display:block}
#app-splash-hex{position:absolute;left:50%;top:var(--app-splash-cy,50%);width:${APP_SPLASH_HEX.width}px;height:${APP_SPLASH_HEX.height}px;margin:-${APP_SPLASH_HEX.height / 2}px 0 0 -${APP_SPLASH_HEX.width / 2}px;transform:translateY(0px)}
#app-splash-type{position:absolute;top:var(--app-splash-cy,50%);left:50%;margin-top:-17px;font:900 26px/34px P24Splash,IRANSansXV,tahoma;color:#3F3F79;white-space:nowrap;opacity:0;transform:translate(var(--app-splash-tx,0px),0px) translateX(14px)}
.app-splash-on #app-splash-hex{transition:transform .35s cubic-bezier(.4,0,.2,1);transform:translate(var(--app-splash-hx,0px),0px) scale(.5)}
.app-splash-on #app-splash-type{transition:opacity .35s ease-out .2s,transform .35s cubic-bezier(.4,0,.2,1) .2s;opacity:1;transform:translate(var(--app-splash-tx,0px),0px)}
#app-splash-loader{position:absolute;left:50%;bottom:calc(56px + env(safe-area-inset-bottom,0px));width:34px;height:34px;margin-left:-17px;color:#a3a3a3}
#app-splash-loader circle{animation:app-splash-dot 2s ease-in-out infinite}#app-splash-loader circle:nth-of-type(1){animation-delay:-.4s}#app-splash-loader circle:nth-of-type(2){animation-delay:-.2s}
@keyframes app-splash-dot{0%,100%{opacity:1}50%{opacity:.25}}`;

export const APP_SPLASH_LOADER_SVG =
  '<svg id="app-splash-loader" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" role="status" aria-label="در حال بارگذاری"><circle fill="currentColor" r="16" cx="40" cy="100"/><circle fill="currentColor" r="16" cx="100" cy="100"/><circle fill="currentColor" r="16" cx="160" cy="100"/></svg>';

/** Native splash stays this long on the web before the logo starts moving (reads as one splash). */
const HOLD_MS = 250;
/** Shrink + slide + wordmark, then a beat to read it, before the splash may fade. */
const ANIMATION_MS = 550 + 450;
const FADE_MS = 300;
const GAP_PX = 8;

/**
 * Starts the logo move once the wordmark font is ready (or after a short cap), and hides the
 * splash with a fade when home is ready — never before the move has finished.
 */
export const APP_SPLASH_INLINE_SCRIPT = `(function(){
var S=document.getElementById('${APP_SPLASH_ID}');if(!S)return;
var t=document.getElementById('app-splash-type'),done=0,want=0,t0=Date.now();
function hide(){var d=window.${APP_SPLASH_DOCTOR_HOME_KEY};if(d){location.replace(d);return}S.style.transition='opacity ${FADE_MS}ms ease-out';S.style.opacity='0';S.style.pointerEvents='none';setTimeout(function(){S.remove()},${FADE_MS + 20})}
function go(){if(go.d)return;go.d=1;
var hw=${APP_SPLASH_HEX.width}/2,w=hw+${GAP_PX}+t.getBoundingClientRect().width;
S.style.setProperty('--app-splash-hx',(w/2-hw/2)+'px');S.style.setProperty('--app-splash-tx',(-w/2)+'px');
var still=matchMedia('(prefers-reduced-motion: reduce)').matches;
setTimeout(function(){S.classList.add('app-splash-on');setTimeout(function(){done=1;if(want||window.${APP_SPLASH_DOCTOR_HOME_KEY})hide()},still?0:${ANIMATION_MS})},still?0:Math.max(0,${HOLD_MS}-(Date.now()-t0)))}
if(document.fonts&&document.fonts.load){document.fonts.load('900 26px P24Splash').then(go,go)}else go();
setTimeout(go,600);
window.__hideAppSplash=function(){window.__hideAppSplash=function(){};want=1;if(done)hide()};
setTimeout(function(){done=1;window.__hideAppSplash();hide()},${MAX_VISIBLE_MS});})();`;

export const hideAppSplash = () => {
  if (typeof window === 'undefined') return;
  (window as unknown as { __hideAppSplash?: () => void }).__hideAppSplash?.();
};
